/**
 * THE COUNTER TERMINAL — the card machine, without the machine.
 *
 * Somebody is standing at the desk with a card and a job that is finished.
 * The advisor picks the invoice, keys the card, and it is done. That is
 * the whole feature.
 *
 * IT USES THE SAME FORM AND THE SAME ENDPOINT as the customer's own page.
 * The card is tokenised in THIS browser by Accept.js and only a one-time
 * nonce is posted; the advisor's screen never holds a card number and
 * neither does the server, which refuses anything shaped like one. A
 * second way to take money is a second place for money to go missing, so
 * there is one, and this is a different door onto it rather than another
 * road.
 *
 * The customer's portal token is the identity `/api/pay` keys on. Staff
 * can read it — it is on the customer record they already have — so the
 * terminal hands it over exactly as the customer's own page does. That
 * keeps the server with one rule to enforce instead of two.
 *
 * WHAT IT WILL NOT DO: take a payment on an invoice that is already
 * settled, or on a job with no invoice at all. Both are how a shop ends
 * up charging somebody twice.
 */

import { db } from "../data/client.ts";
import { ensurePortalToken } from "../data/jobs.ts";
import { esc, money, mount, ago, since } from "../ui/dom.ts";
import { findBarHtml, wireFindBar } from "../ui/findbar.ts";
import type { Role } from "../domain/permissions.ts";

const MAY_TAKE: Role[] = ["admin", "supervisor", "advisor"];

type Open = {
  id: string; invoice_number: string; total: number; issued_at: string;
  jobs: { id: string; job_number: string;
    customers: { id: string; full_name: string; portal_token: string | null } | null } | null;
};

declare global {
  interface Window {
    cardForm?: (host: HTMLElement, opts: {
      token: string; invoiceId: string; amount: number;
      label?: string; onPaid?: (out: unknown) => void;
    }) => void;
  }
}

/** Accept.js only exists on the page once it is asked for. */
function ensurePayScript(): void {
  if (document.getElementById("payjs")) return;
  const s = document.createElement("script");
  s.id = "payjs";
  s.src = "/pay.js";
  document.head.appendChild(s);
}

export async function terminalPage(role: Role = "viewer"): Promise<void> {
  const mayTake = MAY_TAKE.includes(role);
  ensurePayScript();

  mount(`<div class="page">
    <header class="page-head"><a class="back" href="#/more">&larr; Everything else</a>
      <h1>Counter</h1>
      <p>${mayTake
        ? "Somebody at the desk with a card. Pick the invoice and key it in."
        : "Reading only — your login cannot take payments."}</p></header>
    <div id="findhost">${findBarHtml("Find an invoice — name or number")}</div>
    <div id="tlist"><div class="loading">Reading&hellip;</div></div>
  </div>`);

  const host = document.getElementById("tlist");
  if (!host) return;

  try {
    const sb = await db();
    const { data, error } = await sb.from("invoices")
      .select("id,invoice_number,total,issued_at," +
        "jobs(id,job_number,customers(id,full_name,portal_token))")
      .eq("status", "open")
      .order("issued_at", { ascending: false })
      .limit(200);
    if (error) throw new Error(error.message);

    const open = (data ?? []) as unknown as Open[];
    if (!open.length) {
      host.innerHTML = `<div class="card"><div class="empty">
        Nothing is owed. Every invoice is settled. &#127942;</div></div>`;
      return;
    }

    host.innerHTML = `<div class="card">
      <div class="card-head">Owed &middot; ${open.length}</div>
      ${open.map((i) => {
        const who = i.jobs?.customers?.full_name ?? "No customer";
        return `
          <div class="row tinv" data-inv="${esc(i.id)}">
            <div class="row-main">
              <div class="row-title"><span class="num">${esc(who)}</span>
                <span class="pill">${esc(i.invoice_number)}</span></div>
              <div class="row-sub">${i.jobs
                ? `<a href="#/job/${esc(i.jobs.id)}">${esc(i.jobs.job_number)}</a>` : ""}
                <span class="dot">&middot;</span>raised ${esc(since(i.issued_at))}</div>
            </div>
            <div class="row-side">
              <div class="amount">${money(i.total)}</div>
              ${mayTake ? `<button class="btn sm" data-take="${esc(i.id)}">Take a card</button>`
                : ""}
            </div>
          </div>
          <div class="tpay" id="t-${esc(i.id)}"></div>`;
      }).join("")}
    </div>`;

    wireFindBar({ hostId: "findhost", listId: "tlist", noun: "invoice", rowSelector: ".tinv" });

    host.querySelectorAll<HTMLButtonElement>("[data-take]").forEach((b) =>
      b.addEventListener("click", async () => {
        const id = b.dataset["take"] ?? "";
        const inv = open.find((x) => x.id === id);
        const slot = document.getElementById(`t-${id}`);
        if (!inv || !slot) return;

        // Pressed twice is a closed drawer, not a second form.
        if (slot.innerHTML.trim()) { slot.innerHTML = ""; b.textContent = "Take a card"; return; }

        const custId = inv.jobs?.customers?.id;
        if (!custId) {
          slot.innerHTML = `<p class="msg bad">This invoice has no customer on it.</p>`;
          return;
        }
        b.textContent = "Close";
        slot.innerHTML = `<div class="loading">Getting ready&hellip;</div>`;
        // Made on the spot when it is missing, rather than sending somebody
        // off to save a record to create a link they cannot see.
        const token = await ensurePortalToken(custId);
        if (!token) {
          slot.innerHTML = `<p class="msg bad">Could not make a payment link for this
            customer. Take the card on the terminal and record it on the job.</p>`;
          b.textContent = "Take a card";
          return;
        }
        window.cardForm?.(slot, {
          token, invoiceId: inv.id, amount: Number(inv.total),
          label: `owed on ${inv.invoice_number}`,
          onPaid: () => { setTimeout(() => void terminalPage(role), 2200); },
        });
      }));
  } catch (e) {
    host.innerHTML = `<div class="card"><div class="empty bad">${
      esc(e instanceof Error ? e.message : String(e))}</div></div>`;
  }
}
